import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Copy, GitCommit, ThumbsUp, Pencil, Check } from 'lucide-react';
import {
  useGetMyActivity,
  useGetWeeklyRecap,
  useGetWeeklyRecapDraft,
  usePutWeeklyRecap,
} from '@edison/api-client-react';
import type { DeveloperRecapNote, DeveloperRecapNoteKind } from '@edison/api-client-react';
import { Button } from '@/components/ui/button.js';
import { InlineError } from '@/components/ui/inline-error.js';
import { SkeletonRow } from '@/components/ui/skeleton.js';

// Monday (local) of the week containing `d`, as YYYY-MM-DD.
function mondayOf(d: Date): string {
  const dow = (d.getDay() + 6) % 7; // Mon=0 … Sun=6
  const mon = new Date(d);
  mon.setDate(d.getDate() - dow);
  mon.setHours(0, 0, 0, 0);
  return `${mon.getFullYear()}-${String(mon.getMonth() + 1).padStart(2, '0')}-${String(mon.getDate()).padStart(2, '0')}`;
}

function shiftWeek(weekStart: string, deltaWeeks: number): string {
  const d = new Date(`${weekStart}T00:00:00`);
  d.setDate(d.getDate() + deltaWeeks * 7);
  return mondayOf(d);
}

function weekLabel(weekStart: string): string {
  const mon = new Date(`${weekStart}T00:00:00`);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  return `${mon.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} – ${sun.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

// Review-and-confirm framing (Addendum v2). We PRE-DRAFT from the week's real work products and the
// dev just confirms; edits/additions are the dev's own words (the AI never writes to `notes`).
// The `flag` bucket is always dev-authored — the draft leaves it empty (forward-looking, not
// inferable from past work).
const PROMPTS: { kind: DeveloperRecapNoteKind; title: string; hint: string; placeholder: string }[] = [
  { kind: 'shipped', title: 'What you got done', hint: 'Drafted from your commits and plans — tweak if needed.', placeholder: 'e.g. shipped the takeoff metrics dashboard and closed the 500-error path' },
  { kind: 'figured_out', title: 'What you cracked or figured out', hint: 'Drafted from reworks and dropped spikes — your call.', placeholder: 'e.g. cracked the takeoff pipeline after 3 attempts' },
  { kind: 'flag', title: "Anything to flag for next week?", hint: "We can't see this one — add it if there's something.", placeholder: 'e.g. OCR provider still flaky under load — worth a spike' },
];

type NoteDraft = { text: string; projectIds: string[] };
type DraftMap = Record<DeveloperRecapNoteKind, NoteDraft>;

const emptyDrafts = (): DraftMap => ({
  shipped: { text: '', projectIds: [] },
  figured_out: { text: '', projectIds: [] },
  flag: { text: '', projectIds: [] },
});

type StatusMsg = { kind: 'ok' | 'saving' | 'error'; text: string } | null;

export function WeeklyRecapPage() {
  const [status, setStatus] = useState<StatusMsg>(null);
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const isCurrentWeek = weekStart === mondayOf(new Date());

  const { data: activityData, isLoading: activityLoading } = useGetMyActivity({ range: 'week' });
  const { data: draftData, isLoading: draftLoading, error: draftError, refetch: refetchDraft } = useGetWeeklyRecapDraft({ weekStart });
  const { data: recapData, isLoading: recapLoading, error: recapError, refetch: refetchRecap } = useGetWeeklyRecap({ weekStart });
  const saveMutation = usePutWeeklyRecap();

  const activity = activityData?.data;
  const days = useMemo(() => activity?.days ?? [], [activity]);
  const totals = activity?.totals ?? { hours: 0, commits: 0, projects: 0, sessions: 0 };

  const savedRecap = recapData?.data;
  const aiDraft = draftData?.data?.draft ?? { shipped: '', figured_out: '' };
  const draftIsEmpty = !aiDraft.shipped.trim() && !aiDraft.figured_out.trim();
  // The saved recap is authoritative for confirmed state; fall back to the draft endpoint's status.
  const recapStatus = savedRecap?.status ?? draftData?.data?.status ?? 'pending';
  const isConfirmed = recapStatus === 'confirmed' || recapStatus === 'edited';

  // Distinct projects touched this week (for the shipped digest + tag chips).
  const weekProjects = useMemo(() => {
    const byId = new Map<string, string>();
    for (const d of days) for (const p of d.projects ?? []) {
      if (p.projectId) byId.set(p.projectId, p.project ?? '(no project)');
    }
    return [...byId.entries()].map(([id, name]) => ({ id, name }));
  }, [days]);

  // Shipped digest: commit subjects grouped by project, deduped across the week, capped per project.
  const SHIPPED_CAP = 6;
  const shippedByProject = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const d of days) for (const p of d.projects ?? []) {
      const name = p.project ?? '(no project)';
      const set = map.get(name) ?? new Set<string>();
      for (const c of p.commits ?? []) if (c.message) set.add(c.message);
      if (set.size) map.set(name, set);
    }
    return [...map.entries()]
      .sort((a, b) => b[1].size - a[1].size)
      .map(([name, set]) => { const all = [...set]; return { name, shown: all.slice(0, SHIPPED_CAP), more: Math.max(0, all.length - SHIPPED_CAP) }; });
  }, [days]);

  // ─── edit mode ──────────────────────────────────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState<DraftMap>(emptyDrafts);

  // Reset the view whenever the week changes (fresh fetches drive the new week's state).
  useEffect(() => { setEditing(false); setStatus(null); }, [weekStart]);

  // Seed the edit textareas: from the dev's saved notes if they've already confirmed/edited,
  // otherwise from the AI draft (shipped + figured_out; flag always starts empty for the dev).
  const seedFromSaved = useCallback((): DraftMap => {
    const next = emptyDrafts();
    for (const n of savedRecap?.notes ?? []) {
      if (n.kind in next) next[n.kind] = { text: n.text ?? '', projectIds: n.projectIds ?? [] };
    }
    return next;
  }, [savedRecap]);

  const seedFromDraft = useCallback((): DraftMap => ({
    shipped: { text: aiDraft.shipped.trim(), projectIds: [] },
    figured_out: { text: aiDraft.figured_out.trim(), projectIds: [] },
    flag: { text: '', projectIds: [] },
  }), [aiDraft]);

  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  const weekStartRef = useRef(weekStart);
  weekStartRef.current = weekStart;
  const projectContextRef = useRef<string[]>([]);
  projectContextRef.current = weekProjects.map((p) => p.name);
  const saveMutationRef = useRef(saveMutation);
  saveMutationRef.current = saveMutation;

  const buildNotes = (d: DraftMap): DeveloperRecapNote[] =>
    PROMPTS
      .map((p) => ({ kind: p.kind, text: d[p.kind].text.trim(), projectIds: d[p.kind].projectIds }))
      .filter((n) => n.text.length > 0);

  // Persist the given notes with a status; refetch so the confirmed/edited state re-renders.
  const persist = useCallback((notes: DeveloperRecapNote[], newStatus: 'confirmed' | 'edited') => {
    setStatus({ kind: 'saving', text: 'Saving…' });
    saveMutationRef.current.mutate(
      { data: { weekStart: weekStartRef.current, notes, projectContext: projectContextRef.current, status: newStatus } },
      {
        onSuccess: () => { setStatus({ kind: 'ok', text: 'Saved' }); void refetchRecap(); void refetchDraft(); },
        onError: () => setStatus({ kind: 'error', text: 'Save failed — your notes are still here, keep editing to retry' }),
      },
    );
  }, [refetchRecap, refetchDraft]);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushSave = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    persist(buildNotes(draftsRef.current), 'edited');
  }, [persist]);
  const scheduleSave = useCallback(() => {
    setStatus({ kind: 'saving', text: 'Saving…' });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => flushSave(), 800);
  }, [flushSave]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  // ─── actions ──────────────────────────────────────────────────────────────
  const handleLooksRight = () => {
    const notes = buildNotes(seedFromDraft());
    persist(notes, 'confirmed');
  };
  const handleAdjust = () => { setDrafts(seedFromDraft()); setEditing(true); setStatus(null); };
  const handleEditAgain = () => { setDrafts(seedFromSaved()); setEditing(true); setStatus(null); };
  const handleDone = () => { flushSave(); setEditing(false); };

  const setText = (kind: DeveloperRecapNoteKind, text: string) => {
    setDrafts((prev) => ({ ...prev, [kind]: { ...prev[kind], text } }));
    scheduleSave();
  };
  const toggleProject = (kind: DeveloperRecapNoteKind, projectId: string) => {
    setDrafts((prev) => {
      const cur = prev[kind].projectIds;
      const next = cur.includes(projectId) ? cur.filter((id) => id !== projectId) : [...cur, projectId];
      return { ...prev, [kind]: { ...prev[kind], projectIds: next } };
    });
    scheduleSave();
  };

  const autoGrow = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  // The notes shown/copied when NOT editing: the dev's confirmed notes if they exist, else the AI
  // draft (shipped + figured_out) as the review scaffold.
  const displayNotes = useMemo((): { kind: DeveloperRecapNoteKind; text: string; projectIds: string[] }[] => {
    if (isConfirmed && savedRecap?.notes?.length) {
      return savedRecap.notes.map((n) => ({ kind: n.kind, text: n.text ?? '', projectIds: n.projectIds ?? [] }));
    }
    return [
      { kind: 'shipped' as const, text: aiDraft.shipped.trim(), projectIds: [] },
      { kind: 'figured_out' as const, text: aiDraft.figured_out.trim(), projectIds: [] },
    ].filter((n) => n.text.length > 0);
  }, [isConfirmed, savedRecap, aiDraft]);

  const handleCopy = async () => {
    const lines: string[] = [];
    lines.push(`# My week — ${weekLabel(weekStart)}`);
    lines.push('');
    lines.push(`${totals.hours}h captured · ${totals.commits} commits · ${totals.projects} projects`);
    if (shippedByProject.length) {
      lines.push('');
      lines.push('## Shipped');
      for (const { name, shown, more } of shippedByProject) {
        lines.push(`**${name}**`);
        for (const m of shown) lines.push(`- ${m}`);
        if (more > 0) lines.push(`- …and ${more} more`);
      }
    }
    for (const prompt of PROMPTS) {
      const note = displayNotes.find((n) => n.kind === prompt.kind);
      if (!note?.text) continue;
      const tags = note.projectIds
        .map((id) => weekProjects.find((p) => p.id === id)?.name)
        .filter((n): n is string => Boolean(n));
      const tag = tags.length ? ` _(${tags.join(', ')})_` : '';
      lines.push('');
      lines.push(`## ${prompt.title}`);
      lines.push(`- ${note.text}${tag}`);
    }
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setStatus({ kind: 'ok', text: 'Recap copied to clipboard' });
    } catch {
      setStatus({ kind: 'error', text: 'Could not access the clipboard' });
    }
  };

  const statusPill = status && (
    <span
      role="status"
      className={`text-xs ${
        status.kind === 'ok'
          ? 'text-[oklch(0.50_0.14_155)]'
          : status.kind === 'saving'
            ? 'text-muted-foreground'
            : 'text-[oklch(0.55_0.18_25)]'
      }`}
    >
      {status.text}
    </span>
  );

  const reflectionLoading = draftLoading || recapLoading;
  const reflectionError = draftError || recapError;

  return (
    <div className="max-w-[1320px] mx-auto px-8 py-6 lg:px-10 animate-page">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold tracking-tight text-foreground mb-1">Weekly Recap</h1>
          <p className="text-sm text-muted-foreground">A look back at what you got done this week</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="secondary" size="sm" aria-label="Previous week" onClick={() => setWeekStart((w) => shiftWeek(w, -1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium text-foreground min-w-[9rem] text-center">{weekLabel(weekStart)}</span>
          <Button variant="secondary" size="sm" aria-label="This week" disabled={isCurrentWeek} onClick={() => setWeekStart(mondayOf(new Date()))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stat row */}
      <div className="mb-6 rounded-[14px] border border-border bg-card px-5 py-4">
        {activityLoading ? (
          <SkeletonRow />
        ) : (
          <p className="text-sm text-foreground">
            <span className="font-semibold">{totals.hours}h</span> captured
            <span className="text-muted-foreground"> · </span>
            <span className="font-semibold">{totals.commits}</span> commits
            <span className="text-muted-foreground"> · </span>
            <span className="font-semibold">{totals.projects}</span> projects
            {!isCurrentWeek && <span className="text-muted-foreground"> · scaffold shows the current week</span>}
          </p>
        )}
      </div>

      {/* Shipped digest */}
      {isCurrentWeek && shippedByProject.length > 0 && (
        <section className="mb-8">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">What shipped</h2>
          <div className="space-y-4">
            {shippedByProject.map(({ name, shown, more }) => (
              <div key={name} className="rounded-[14px] border border-border bg-card px-4 py-3">
                <p className="text-sm font-medium text-foreground mb-2">{name}</p>
                <ul className="space-y-1">
                  {shown.map((m, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                      <GitCommit className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
                      <span className="min-w-0 break-words">{m}</span>
                    </li>
                  ))}
                  {more > 0 && (
                    <li className="text-xs text-muted-foreground pl-[22px]">…and {more} more</li>
                  )}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Review-and-confirm */}
      <section className="mb-8">
        <div className="mb-4">
          <h2 className="text-sm font-semibold text-foreground">Your recap</h2>
          <p className="text-xs text-muted-foreground">We drafted this from your week. Give it a thumbs-up or tweak it — about a minute.</p>
        </div>

        {reflectionLoading ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <SkeletonRow key={i} />)}</div>
        ) : reflectionError ? (
          <InlineError message="Failed to load your recap." onRetry={() => { void refetchDraft(); void refetchRecap(); }} />
        ) : editing ? (
          /* ── Edit mode: buckets become editable, pre-filled ── */
          <div className="space-y-6">
            {PROMPTS.map((prompt) => {
              const draft = drafts[prompt.kind];
              return (
                <div key={prompt.kind}>
                  <label htmlFor={`recap-${prompt.kind}`} className="block text-sm font-medium text-foreground">{prompt.title}</label>
                  <p className="text-xs text-muted-foreground mb-2">{prompt.hint}</p>
                  <textarea
                    id={`recap-${prompt.kind}`}
                    ref={autoGrow}
                    value={draft.text}
                    onChange={(e) => { setText(prompt.kind, e.target.value); autoGrow(e.currentTarget); }}
                    onBlur={() => { if (timer.current) flushSave(); }}
                    rows={2}
                    placeholder={prompt.placeholder}
                    className="w-full rounded-[10px] border border-border bg-background px-3 py-2 text-sm text-foreground resize-none overflow-hidden focus:outline-none focus:ring-2 focus:ring-[oklch(0.50_0.12_240)]/40"
                  />
                  {isCurrentWeek && weekProjects.length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <span className="text-xs text-muted-foreground mr-1">Relates to:</span>
                      {weekProjects.map((p) => {
                        const on = draft.projectIds.includes(p.id);
                        return (
                          <button
                            key={p.id}
                            type="button"
                            aria-pressed={on}
                            onClick={() => toggleProject(prompt.kind, p.id)}
                            className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                              on
                                ? 'border-transparent bg-[oklch(0.50_0.12_240)] text-white'
                                : 'border-border bg-background text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            {p.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="flex items-center gap-3">
              <Button onClick={handleDone}>
                <Check className="h-4 w-4 mr-1" /> Done
              </Button>
              {statusPill}
            </div>
          </div>
        ) : draftIsEmpty && !isConfirmed ? (
          /* ── Rule Zero: no evidence this week — never fabricate; invite the dev to add ── */
          <div className="rounded-[14px] border border-dashed border-border bg-card px-5 py-8 text-center">
            <p className="text-sm text-foreground">We didn't find commits or plans to draft from this week.</p>
            <p className="text-xs text-muted-foreground mt-1 mb-4">Add anything you got done in your own words — all optional.</p>
            <Button variant="secondary" onClick={handleAdjust}>
              <Pencil className="h-4 w-4 mr-1" /> Add your recap
            </Button>
          </div>
        ) : (
          /* ── Review mode: read the draft (or the confirmed notes), one-click confirm ── */
          <div className="space-y-4">
            {isConfirmed && (
              <div className="flex items-center gap-1.5 text-xs font-medium text-[oklch(0.50_0.14_155)]">
                <Check className="h-4 w-4" aria-hidden="true" />
                {recapStatus === 'edited' ? 'Confirmed (edited)' : 'Confirmed'}
              </div>
            )}
            {displayNotes.length > 0 ? (
              displayNotes.map((note) => {
                const prompt = PROMPTS.find((p) => p.kind === note.kind);
                const tags = note.projectIds
                  .map((id) => weekProjects.find((p) => p.id === id)?.name)
                  .filter((n): n is string => Boolean(n));
                return (
                  <div key={note.kind} className="rounded-[14px] border border-border bg-card px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">{prompt?.title ?? note.kind}</p>
                    <p className="text-sm text-foreground break-words">{note.text}</p>
                    {tags.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">{tags.join(', ')}</p>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="rounded-[14px] border border-dashed border-border bg-card px-4 py-6 text-center">
                <p className="text-sm text-muted-foreground">Nothing captured yet for this week.</p>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 pt-1">
              {!isConfirmed ? (
                <>
                  <Button onClick={handleLooksRight}>
                    <ThumbsUp className="h-4 w-4 mr-1" /> Looks right
                  </Button>
                  <Button variant="secondary" onClick={handleAdjust}>
                    <Pencil className="h-4 w-4 mr-1" /> Add / adjust
                  </Button>
                </>
              ) : (
                <Button variant="secondary" onClick={handleEditAgain}>
                  <Pencil className="h-4 w-4 mr-1" /> Edit again
                </Button>
              )}
              {statusPill}
            </div>
          </div>
        )}
      </section>

      <div className="flex items-center gap-3">
        <Button variant="secondary" onClick={handleCopy}>
          <Copy className="h-4 w-4 mr-1" /> Copy summary
        </Button>
      </div>
    </div>
  );
}
